import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { AuthViewModel } from '../../../viewModels/auth-view-model';
import { ActivatedRoute, Router } from '@angular/router';
import { SignInRequest } from '../../../../../models/account/requests/sign-in-request';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { BsError } from '../../../../../models/shared/bs-error';
import { BaseComponent } from '../../../../../models/base/base-component';
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs';
import { ReactiveFormEmailComponent, ReactiveFormPasswordComponent } from '@mobilefirstdev/reactive-form';
import { map } from 'rxjs/operators';
import { ToastService } from '../../../../../modules/toasts/service/toast.service';
import { exists } from '../../../../../functions/exists';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss', '../auth.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SignInComponent extends BaseComponent {
  constructor(
    public viewModel: AuthViewModel,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService
  ) {
    super();
  }

  @ViewChild(ReactiveFormEmailComponent) emailComponent: ReactiveFormEmailComponent;
  @ViewChild(ReactiveFormPasswordComponent) passwordComponent: ReactiveFormPasswordComponent;
  private deleteEmailAstrixMutationObserver: MutationObserver;
  private deletePasswordAstrixMutationObserver: MutationObserver;
  // View
  private _loadingOpts = new BehaviorSubject(LoadingOptions.default());
  public loadingOpts$ = this._loadingOpts as Observable<LoadingOptions>;
  public isLoading$ = this._loadingOpts.pipe(map(opts => opts.isLoading));
  // Variables
  public readonly req$ = combineLatest([this.viewModel.preFillEmail$, this.viewModel.preFillPassword$]).pipe(
    map(([email, password]) => new SignInRequest(email, password))
  );
  private deleteMutationConfig = { attributes: false, childList: true, subtree: true };

  private deleteAstrixBlock = (div: HTMLDivElement): void => {
    const deleteMe = div?.getElementsByClassName('reactive-form-required-star');
    for (const astrix of deleteMe[Symbol.iterator]()) {
      astrix.remove();
    }
  };

  override setupBindings() {
    this.bindUrlParams();
    this.deleteEmailAstrix();
    this.deletePasswordAstrix();
  }

  bindUrlParams() {}

  // View Actions

  formSubmitted(req: SignInRequest) {
    if (!this._loadingOpts.containsRequest(this.viewModel.signingInMsg)) {
      this._loadingOpts.addRequest(this.viewModel.signingInMsg);
      this.viewModel.signIn$(req).subscribe({
        error: (error: BsError) => {
          this._loadingOpts.removeRequest(this.viewModel.signingInMsg);
          this.toastService.publishError(error);
          throwError(() => error);
        }
      });
    }
  }

  secondaryButtonPressed() {
    const tempEmail = this.emailComponent?.getMyValue();
    this.router.navigate(['auth/forgot-password'], { queryParams: { email: tempEmail } }).then();
  }

  private deleteEmailAstrix(): void {
    const emailDiv = this.emailComponent.getNativeElement() as HTMLDivElement;
    if (exists(emailDiv)) {
      const callback = () => this.deleteAstrixBlock(emailDiv);
      this.deleteEmailAstrixMutationObserver = new MutationObserver(callback);
      this.deleteEmailAstrixMutationObserver.observe(emailDiv, this.deleteMutationConfig);
    }
  }

  private deletePasswordAstrix(): void {
    const passwordDiv = this.passwordComponent.getNativeElement() as HTMLDivElement;
    if (exists(passwordDiv)) {
      const callback = () => this.deleteAstrixBlock(passwordDiv);
      this.deletePasswordAstrixMutationObserver = new MutationObserver(callback);
      this.deletePasswordAstrixMutationObserver.observe(passwordDiv, this.deleteMutationConfig);
    }
  }

  override destroy() {
    super.destroy();
    this.deleteEmailAstrixMutationObserver?.disconnect();
    this.deletePasswordAstrixMutationObserver?.disconnect();
  }
}
